import React, { useContext, useEffect, useMemo, useState } from 'react'
import API from '../../../services/API'
import { TenantSettingsContext } from '../../../App'

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import BusinessIcon from '@mui/icons-material/Business'
import { AddCircle, Edit } from '@mui/icons-material'

import BigModal from '../../../components/Modals/BigModal'
import AddNewClient from '../../clients/AddNewClient'
import AddNewContact from '../../clients/ClientPage/AddNewContact'

import { BookingContext, SectionHeader } from './NewBooking'
import OrganisationSelector from './OrganisationSelector'
import OrganisationWardSelector from './OrganisationWardSelector'

const getId = (value) => (typeof value === 'string' ? value : value?._id)

const OrganisationRequester = () => {
  const { formData, setFormData, handleChange, selectedPatient, orgHasWards } =
    useContext(BookingContext)

  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const [organisationOptions, setOrganisationOptions] = useState([])
  const [contactOptions, setContactOptions] = useState([])
  const [portalRequesterOptions, setPortalRequesterOptions] = useState([])
  const [userOptions, setUserOptions] = useState([])

  const [openAddNewOrganisation, setOpenAddNewOrganisation] = useState(false)
  const [openAddNewContact, setOpenAddNewContact] = useState(false)

  const organisationId = getId(formData?.organisation)
  const wardDepartmentId = getId(formData?.wardDepartment)

  const selectedOrganisation = useMemo(() => {
    if (!organisationId) return null

    return organisationOptions.find((org) => org._id === organisationId) || null
  }, [organisationId, organisationOptions])

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [organisationRes, employeeRes] = await Promise.all([
          API.get('/pts/organisationoptions?type=PTS'),
          API.get('/pts/employeeoptions'),
        ])

        if (organisationRes.status === 200) {
          setOrganisationOptions(organisationRes.data)
        }

        if (employeeRes.status === 200) {
          setUserOptions(employeeRes.data)
        }
      } catch (error) {
        console.error('Failed to load organisation/requester options:', error)
      }
    }

    loadOptions()
  }, [])

  useEffect(() => {
    const loadPortalRequesterOptions = async () => {
      if (
        !organisationId ||
        !wardDepartmentId ||
        formData?.patientIsCustomer
      ) {
        setPortalRequesterOptions([])
        return
      }

      try {
        const res = await API.get(
          `/pts/requesteroptions?organisation=${organisationId}&ward=${wardDepartmentId}`,
        )

        if (res.status === 200) {
          setPortalRequesterOptions(res.data)
        }
      } catch (error) {
        console.error('Failed to load requester options:', error)
      }
    }

    loadPortalRequesterOptions()
  }, [organisationId, wardDepartmentId, formData?.patientIsCustomer])

  // Saved contacts of the selected organisation (e.g. council social workers
  // who request transfers but have no portal login). Added contacts persist
  // as Contact docs, so they reappear here on the next booking.
  useEffect(() => {
    const loadContactOptions = async () => {
      if (!organisationId || formData?.patientIsCustomer) {
        setContactOptions([])
        return
      }

      try {
        const res = await API.get(
          `/pts/contactoptions?organisation=${organisationId}`,
        )

        if (res.status === 200) {
          setContactOptions(res.data)
        }
      } catch (error) {
        console.error('Failed to load contact options:', error)
      }
    }

    loadContactOptions()
  }, [organisationId, formData?.patientIsCustomer])

  const requestedByOptions = useMemo(() => {
    const portalRequesters = portalRequesterOptions.map((requester) => ({
      ...requester,
      model: 'User',
      group: selectedOrganisation?.orgName || 'Portal users',
    }))

    const contacts = contactOptions.map((contact) => ({
      ...contact,
      model: 'contact',
      group: selectedOrganisation?.orgName || 'Contacts',
    }))

    const users = userOptions.map((user) => ({
      ...user,
      model: 'User',
      group: orgSettings?.orgName || 'Users',
    }))

    const optionsByKey = new Map()
    const allOptions = [...portalRequesters, ...contacts, ...users]

    allOptions.forEach((option) => {
      optionsByKey.set(`${option.model}-${option._id}`, option)
    })

    return [...optionsByKey.values()]
  }, [
    portalRequesterOptions,
    contactOptions,
    userOptions,
    selectedOrganisation,
    orgSettings?.orgName,
  ])

  const requestedByValue = useMemo(() => {
    if (!Array.isArray(formData?.requestedBy)) return []

    return requestedByOptions.filter((option) =>
      formData.requestedBy.some(
        (selected) =>
          selected._id === option._id && selected.model === option.model,
      ),
    )
  }, [formData?.requestedBy, requestedByOptions])

  const patientHasNoContactDetails =
    formData?.patientIsCustomer &&
    selectedPatient &&
    !selectedPatient?.demographics?.email &&
    !selectedPatient?.demographics?.phoneNumber1 &&
    !selectedPatient?.demographics?.phoneNumber2

  return (
    <Box>
      <SectionHeader
        icon={<BusinessIcon fontSize="small" />}
        title="Organisation & Requester"
      />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            label="Patient is the Customer"
            control={
              <Checkbox
                checked={Boolean(formData?.patientIsCustomer)}
                onChange={(event) => {
                  handleChange('patientIsCustomer', event.target.checked)

                  if (event.target.checked) {
                    handleChange('organisation', null)
                    handleChange('wardDepartment', null)
                    handleChange('requestedBy', [])
                  }
                }}
              />
            }
          />
        </Grid>

        {patientHasNoContactDetails && (
          <Grid item xs={12}>
            <Alert severity="error" variant="filled">
              Patient has no contact email or phone number for sending T&Cs for
              signing.
            </Alert>
          </Grid>
        )}

        {!formData?.patientIsCustomer && (
          <>
            <Grid item xs={12} lg={4}>
              <OrganisationSelector
                value={formData?.organisation}
                options={organisationOptions}
                onChange={(organisation) => {
                  handleChange('organisation', organisation)
                  handleChange('wardDepartment', null)
                  handleChange('requestedBy', [])
                }}
                onAddOrganisation={() => setOpenAddNewOrganisation(true)}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <OrganisationWardSelector
                value={formData?.wardDepartment}
                onChange={(ward) => {
                  handleChange('wardDepartment', ward)
                  handleChange('requestedBy', [])
                }}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Autocomplete
                multiple
                options={requestedByOptions}
                value={requestedByValue}
                groupBy={(option) => option.group}
                // Ward-less orgs (councils, brokers) have no ward to pick, so
                // only wait for one where the org actually has wards
                disabled={
                  !organisationId ||
                  (orgHasWards !== false && !formData?.wardDepartment)
                }
                autoComplete={false}
                disableClearable
                getOptionLabel={(option) => {
                  const name = option.firstName
                    ? `${option.firstName} ${option.lastName || ''}`.trim()
                    : option.noAccount?.firstName
                      ? `${option.noAccount.firstName} ${
                          option.noAccount.lastName || ''
                        }`.trim()
                      : 'Unknown'

                  const source =
                    option.model === 'User'
                      ? option.ptsBookingPortalAccess?.portalAccessLevel
                        ? `(${option.ptsBookingPortalAccess.portalAccessLevel})`
                        : `(${orgSettings?.orgName || 'User'})`
                      : option.companyRole
                        ? `(${option.companyRole})`
                        : ''

                  return `${name} ${source}`.trim()
                }}
                isOptionEqualToValue={(option, value) =>
                  option?._id === value?._id && option?.model === value?.model
                }
                onChange={(event, newValue) => {
                  handleChange(
                    'requestedBy',
                    newValue.map((value) => ({
                      _id: value._id,
                      model: value.model,
                    })),
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Requested By"
                    helperText={
                      orgHasWards !== false && !formData?.wardDepartment
                        ? 'Select a Ward/Department first'
                        : ''
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}

                          {organisationId &&
                            (formData?.wardDepartment ||
                              orgHasWards === false) && (
                            <InputAdornment position="end">
                              <Tooltip
                                title={`Add a new contact to ${
                                  selectedOrganisation?.orgName ||
                                  'this organisation'
                                }`}
                              >
                                <IconButton
                                  onClick={() => setOpenAddNewContact(true)}
                                >
                                  <AddCircle color="anGreen" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const name =
                      option.firstName && option.lastName
                        ? `${option.firstName} ${option.lastName}`
                        : option.noAccount?.firstName &&
                            option.noAccount?.lastName
                          ? `${option.noAccount.firstName} ${option.noAccount.lastName}`
                          : 'Unknown'

                    return (
                      <Chip
                        {...getTagProps({ index })}
                        key={`${option.model}-${option._id}`}
                        label={name}
                        color={
                          option.model === 'User' ? 'anPurple' : 'anMainBlue'
                        }
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    )
                  })
                }
              />
            </Grid>
          </>
        )}
      </Grid>

      <BigModal
        open={openAddNewOrganisation}
        onClose={() => setOpenAddNewOrganisation(false)}
      >
        <Stack gap={1}>
          <AddNewClient
            enforceType="PTS"
            customCallbackAction={(newClient) => {
              if (!newClient?._id) return

              const newOrganisation = { ...newClient }

              setOrganisationOptions((prev) =>
                prev.some((org) => org?._id === newOrganisation._id)
                  ? prev
                  : [...prev, newOrganisation],
              )
              handleChange('organisation', newOrganisation)
              setOpenAddNewOrganisation(false)
            }}
          />

          <Button
            onClick={() => setOpenAddNewOrganisation(false)}
            variant="contained"
            color="error"
            size="small"
            fullWidth
          >
            Cancel
          </Button>
        </Stack>
      </BigModal>

      <BigModal
        open={openAddNewContact}
        onClose={() => setOpenAddNewContact(false)}
      >
        <Stack gap={1}>
          <AddNewContact
            client={selectedOrganisation}
            setOpenAddNew={setOpenAddNewContact}
            customCallbackAction={(newContact) => {
              const contactToAdd = {
                ...newContact,
                model: 'contact',
                companyRole: newContact.role,
              }

              setContactOptions((prev) => [...prev, contactToAdd])

              setFormData((prev) => ({
                ...prev,
                requestedBy: [
                  ...(prev.requestedBy || []),
                  { _id: newContact._id, model: 'contact' },
                ],
              }))

              setOpenAddNewContact(false)
            }}
          />
        </Stack>
      </BigModal>
    </Box>
  )
}

export default OrganisationRequester
